import { TOPIC_NOTIFY_CHANGED_RAW, TOPIC_LEADER_CONNECTION_STATUS, TOPIC_FORCE_RECONNECT } from 'lib/ws/constants'
import type { Service } from 'lib/ws/election'
import { getWsHub } from 'lib/ws/hub'
import {
    AuthMessage,
    ChangedRef,
    ClientMessage,
} from 'pbts/c1/api/websocketnotify/v1/websocketnotify'
import { websocketFilterManager } from './websocket-filter-manager'

// Types replicated from existing notify client to keep consistent payloads
export type NotifyChangedEvent = ArrayBuffer

export type ConnectionStatus = 'connected' | 'reconnecting' | 'degraded'

// Retry configuration
const BASE_RETRY_DELAY = 3000
const MAX_RETRY_DELAY = 60000
const DEGRADED_RETRY_THRESHOLD = 3

/**
 * Leader-only service that owns the single /ws/notify socket and broadcasts
 * raw ChangedRef buffers to followers via the hub.
 */
export class NotifyService implements Service {
    public readonly namespace = 'notify'
    private socketInstance: WebSocket | undefined
    private retryCount = 0
    private reconnectTimer: ReturnType<typeof setTimeout> | null = null
    private readonly getAccessToken: () => Promise<string | undefined>

    constructor(getAccessToken: () => Promise<string | undefined>) {
        this.getAccessToken = getAccessToken

        // Initialize the filter manager
        websocketFilterManager.initialize(getAccessToken)
    }

    public async init(): Promise<void> {
        this.setupVisibilityHandler()
        this.setupForceReconnectHandler()
        await this.connect()
    }

    private setupForceReconnectHandler(): void {
        const hub = getWsHub()
        hub?.subscribe(TOPIC_FORCE_RECONNECT, () => {
            // Only leader handles this - reset retry count and reconnect immediately
            this.retryCount = 0
            void this.connect()
        })
    }

    private setupVisibilityHandler(): void {
        if (typeof document === 'undefined') return

        document.addEventListener('visibilitychange', () => {
            if (document.visibilityState !== 'visible') return

            // If tab becomes visible and socket is not open, try to reconnect immediately
            const isSocketDead = !this.socketInstance || this.socketInstance.readyState !== WebSocket.OPEN
            if (isSocketDead) {
                this.retryCount = 0
                void this.connect()
            }
        })
    }

    // eslint-disable-next-line class-methods-use-this
    private broadcastStatus(status: ConnectionStatus): void {
        const hub = getWsHub()
        hub?.publish(TOPIC_LEADER_CONNECTION_STATUS, { status })
    }

    private scheduleReconnect(): void {
        // Clear any existing pending reconnect
        if (this.reconnectTimer !== null) {
            clearTimeout(this.reconnectTimer)
            this.reconnectTimer = null
        }

        const retryTime = Math.min(
            BASE_RETRY_DELAY * 2 ** this.retryCount + Math.floor(Math.random() * BASE_RETRY_DELAY),
            MAX_RETRY_DELAY,
        )

        this.retryCount++

        // Broadcast degraded status after threshold
        if (this.retryCount > DEGRADED_RETRY_THRESHOLD) {
            this.broadcastStatus('degraded')
        } else {
            this.broadcastStatus('reconnecting')
        }

        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null
            void this.connect()
        }, retryTime)
    }

    // No RPC methods are needed for now; followers derive filtering client-side.
    private async connect(): Promise<void> {
        let token: string | undefined
        try {
            token = await this.getAccessToken()
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error('[NotifyService] Failed to get access token, scheduling retry', err)
            this.scheduleReconnect()
            return
        }

        if (!token) {
            // eslint-disable-next-line no-console
            console.warn('[NotifyService] No access token available, scheduling retry')
            this.scheduleReconnect()
            return
        }

        // Clean up any existing send function before creating new socket
        websocketFilterManager.onDisconnect()

        this.socketInstance = new WebSocket(`wss://${location.host}/ws/notify`)

        this.socketInstance.onopen = (): void => {
            // Clear any pending reconnect timer
            if (this.reconnectTimer !== null) {
                clearTimeout(this.reconnectTimer)
                this.reconnectTimer = null
            }

            // Register send function with filter manager
            websocketFilterManager.registerSendFunction((data: Uint8Array) => {
                if (this.socketInstance?.readyState !== WebSocket.OPEN) {
                    // Socket replaced or not ready - signal disconnect to retry later
                    websocketFilterManager.onDisconnect()
                    return
                }
                this.socketInstance.send(data)
            })

            // Send initial auth message
            const authMessage = AuthMessage.create({
                accessToken: token,
            })

            const clientMessage = ClientMessage.create({
                message: {
                    oneofKind: 'auth',
                    auth: authMessage,
                },
            })

            const binaryData = ClientMessage.toBinary(clientMessage)
            this.socketInstance?.send(binaryData)

            this.retryCount = 0
            this.broadcastStatus('connected')
        }

        this.socketInstance.onclose = (event): void => {
            this.socketInstance = undefined

            // Clean up the registered send function to avoid stale closures
            websocketFilterManager.onDisconnect()

            if (event.code !== 1000) {
                // eslint-disable-next-line no-console
                console.error('[NotifyService] WebSocket closed unexpectedly, scheduling retry', {
                    code: event.code,
                    reason: event.reason,
                })
                this.scheduleReconnect()
            }
        }

        this.socketInstance.onerror = (event): void => {
            // eslint-disable-next-line no-console
            console.error('[NotifyService] WebSocket error, scheduling retry', event)
            // Clean up the registered send function to avoid stale closures
            websocketFilterManager.onDisconnect()
        }

        this.socketInstance.onmessage = async (event): Promise<void> => {
            try {
                const d = await event.data.arrayBuffer()

                // Validate the payload is a ChangedRef; if it fails, ignore
                try {
                    ChangedRef.fromBinary(new Uint8Array(d))
                } catch {
                    return
                }

                const hub = getWsHub()
                hub?.publish(TOPIC_NOTIFY_CHANGED_RAW, d as NotifyChangedEvent)
            } catch (err) {
                // eslint-disable-next-line no-console
                console.error('[NotifyService] error reading message', err)
            }
        }
    }
}
